import type { MaskitoOptions } from "@maskito/core";
import {
  maskitoTimeOptionsGenerator,
  maskitoWithPlaceholder,
} from "@maskito/kit";
import { TimeFormat } from "../types";

/**
 * Creates Maskito options for time input with placeholder support.
 *
 * Features:
 * - Time-specific masking for 12/24 hour formats
 * - Placeholder character support
 * - Filtered preprocessors to avoid conflicts
 * - Combined plugins and postprocessors
 *
 * @param format - The time format (12 or 24 hour)
 * @param placeholder - The placeholder string to use
 * @returns Object containing Maskito options and removePlaceholder function
 */
const timePlaceholderMask = ({
  format,
  placeholder,
}: {
  format: TimeFormat;
  placeholder: string;
}) => {
  // Use Maskito Time Kit for time-specific options
  const timeOptions = maskitoTimeOptionsGenerator({
    mode: format === 12 ? "HH:MM AA" : "HH:MM",
    step: 0, // We handle step logic in your business layer
  });

  const { plugins, removePlaceholder, ...placeholderOptions } =
    maskitoWithPlaceholder(placeholder);

  const timePlaceholderMask = {
    ...timeOptions,
    plugins: plugins.concat(timeOptions.plugins || []),
    preprocessors: [
      ...placeholderOptions.preprocessors,
      ...timeOptions.preprocessors,
    ],
    postprocessors: [
      ...timeOptions.postprocessors,
      ...placeholderOptions.postprocessors,
    ],
  } satisfies Required<MaskitoOptions>;

  return { options: timePlaceholderMask, removePlaceholder };
};

export default timePlaceholderMask;
